import "./assets/styles/grid.scss";
import "./assets/styles/basic.scss";
import TextInput from "./components/inputs/textInput";
import TextArea from "./components/inputs/textarea";
import Select from "./components/inputs/select";
import Button from "./components/ui/button";
import CheckBox from "./components/inputs/checkbox";
import Hr from "./components/ui/hr";
import useForm from "./hooks/useForm";
import Alert from "./components/ui/alert";
import Loader from "./components/ui/loader";
import { useEffect, useLayoutEffect, useState } from "react";
import { formatAmount } from "./services/utils";
import { unflatten } from "flat";
import axios from "axios";
import { settings } from "./assets/config/settings";
import { strings } from "./assets/config/translations";

const NUMBER_PATTERN = "^[0-9]*$";
const EMAIL_PATTERN = "^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,4}$";
const PHONE_PATTERN = "^[0-9-+]*$";
const MARKET_CURRENCIES = { dk: "DKK", se: "SEK", fi: "€", no: "NOK" };
// TODO: Function to automatically check errors on input with custom error messages based on inout params (max, min, pattern osv.)

const send = async (data) => {
  try {
    const body = await unflatten(data);
    return await axios.post(`${settings.backendApi}/submit`, body);
  } catch (error) {
    throw error;
  }
};

const GiftcardForm = ({ market, language }) => {
  const [total, setTotal] = useState(0);
  const [minAmountPerGiftcard, setMinAmountPerGiftcard] = useState(250);
  const [maxAmountPerGiftcard, setMaxAmountPerGiftcard] = useState(15000);
  const [invoiceRefSameAsOrder, setInvoiceRefSameAsOrder] = useState(false);

  const config = {
    market,
    language,
    currency: MARKET_CURRENCIES[market],
  };

  const { isLoading, isSubmitted, errorMessage, onSubmit, bind, refs } =
    useForm(send, config);

  const giftcardRender = () => {
    if (refs && refs["nrOfGiftcards"] && refs["amountPerGiftcard"]) {
      const nrOfGiftcards = refs["nrOfGiftcards"].current.value();
      const amountPerGiftcard = refs["amountPerGiftcard"].current.value();
      setTotal(nrOfGiftcards * amountPerGiftcard);
    }
  };

  const invoiceContactRender = () => {
    if (refs && refs["billing.reference.sameAsOrder"]) {
      if (refs["billing.reference.sameAsOrder"].current.value() === true) {
        refs["billing.reference.firstName"].current.reset();
        refs["billing.reference.lastName"].current.reset();
        refs["billing.reference.email"].current.reset();
        refs["billing.reference.phone"].current.reset();
        setInvoiceRefSameAsOrder(true);
      } else {
        setInvoiceRefSameAsOrder(false);
      }
    }
  };

  useLayoutEffect(() => {
    if (market === "fi") {
      setMinAmountPerGiftcard(25);
      setMaxAmountPerGiftcard(1500);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isSubmitted]);

  return (
    <div>
      {isLoading && <Loader />}
      {isSubmitted ? (
        <div className="page">
          <div className="container">
            <div className="row">
              <div className="col col-12">
                <h1>{strings["confirmationTitle"]}</h1>
              </div>
            </div>
            <div className="row">
              <div className="col col-12 col-md-9">
                <div
                  className="text base"
                  dangerouslySetInnerHTML={{
                    __html: strings["confirmationDescription"],
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="page">
          <div className="container">
            <div className="row">
              <div className="col col-12">
                <h1>{strings["companyGiftcardTitle"]}</h1>
              </div>
            </div>
            <div className="row">
              <div className="col col-12 col-md-9">
                <div
                  className="text base"
                  dangerouslySetInnerHTML={{ __html: strings["description"] }}
                />
              </div>
            </div>
          </div>
          <div className="container">
            <div className="row">
              <div className="col col-12 col-md-9 border-box" style={{}}>
                <div className="container ">
                  <div className="row">
                    <div className="col col-12 margin-top ">
                      <h3 className="text lead">{strings["giftcards"]}</h3>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12 col-xs-3">
                      <TextInput
                        name="nrOfGiftcards"
                        label={strings["nrOfGiftcards"]}
                        placeholder={strings["nrOfGiftcardsPlaceholder"]}
                        //   type="number"
                        min={1}
                        max={200}
                        //   trailingLabel={"st"}
                        message={strings["nrOfGiftcardsMessage"]}
                        pattern={NUMBER_PATTERN}
                        onRender={giftcardRender}
                        className="input-right"
                        onInput={(e) => {
                          if (e.target.value === "") {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else if (e.target.value < 1) {
                            e.target.setCustomValidity(
                              strings["errorTooFewGiftcards"]
                            );
                          } else if (e.target.value > 200) {
                            e.target.setCustomValidity(
                              strings["errorTooManyGiftcards"]
                            );
                          } else if (
                            !new RegExp(NUMBER_PATTERN).test(e.target.value)
                          ) {
                            e.target.setCustomValidity(
                              strings["errorInvalidFormat"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                        {...bind}
                        required
                      />
                    </div>
                    <div className="col col-12 col-xs-9">
                      <TextInput
                        name="amountPerGiftcard"
                        placeholder={strings["amountPerGiftcardPlaceholder"]}
                        label={strings["amountPerGiftcard"]}
                        trailingLabel={MARKET_CURRENCIES[market]}
                        onRender={giftcardRender}
                        className="input-right"
                        min={minAmountPerGiftcard}
                        max={maxAmountPerGiftcard}
                        message={`Min ${minAmountPerGiftcard}${
                          market === "fi" ? "€" : "kr"
                        }, max ${maxAmountPerGiftcard}${
                          market === "fi" ? "€" : "kr"
                        }`}
                        pattern={NUMBER_PATTERN}
                        {...bind}
                        required
                        onInput={(e) => {
                          if (e.target.value === "") {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else if (e.target.value < minAmountPerGiftcard) {
                            e.target.setCustomValidity(
                              strings["errorTooLowAmount"]
                            );
                          } else if (e.target.value > maxAmountPerGiftcard) {
                            e.target.setCustomValidity(
                              strings["errorTooHighAmount"]
                            );
                          } else if (
                            !new RegExp(NUMBER_PATTERN).test(e.target.value)
                          ) {
                            e.target.setCustomValidity(
                              strings["errorInvalidFormat"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12">
                      <Select
                        name="occasion"
                        label={strings["occasion"]}
                        placeholder={strings["occasionPlaceholder"]}
                        {...bind}
                        required
                        options={[
                          { value: "retirement", label: strings["retirement"] },
                          { value: "christmas", label: strings["christmas"] },
                          { value: "leaving", label: strings["leaving"] },
                          { value: "birthday", label: strings["birthday"] },
                          {
                            value: "holidaygift",
                            label: strings["holidaygift"],
                          },
                          {
                            value: "anniversary",
                            label: strings["aniversary"],
                          },
                          { value: "other", label: strings["other"] },
                        ]}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12">
                      <TextArea
                        name="greeting"
                        label={`${strings["greeting"]} (${strings["optional"]})`}
                        placeholder={strings["greeting"]}
                        maxLength="500"
                        {...bind}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="container ">
            {/*}
                <div className='row'>
                    <div className='col col-12'>
                        <div className='container'>
                            <div className='row'>
                                <div className='col col-12 '>
                                    <h2 className='text lead large'>{strings["billingInformation"]}</h2>
                                </div>
                            </div>
      
                            <div className='row'>
                                <div className='col col-12 col-md-9'>
                                    <div className='text' dangerouslySetInnerHTML={{ __html: strings["billingInformationDescription"] }} />
                                </div>
                            </div>
                                 
                        </div>
                    </div>
                </div>
                {*/}
            <div className="row">
              <div className="col col-12 col-md-9 ">
                <div className="container ">
                  <div className="row">
                    <div className="col col-12">
                      <h3 className="text lead">{strings["orderContact"]}</h3>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col col-12">
                      <div
                        className="text base"
                        style={{ wordBreak: "keep-all" }}
                        dangerouslySetInnerHTML={{
                          __html: strings["orderContactDescription"],
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12 col-xs-6">
                      <TextInput
                        name="orderContact.firstName"
                        label={strings["firstName"]}
                        placeholder={strings["firstName"]}
                        {...bind}
                        required
                        onInput={(e) => {
                          if (e.target.value === "") {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                      />
                    </div>
                    <div className="col col-12 col-xs-6 ">
                      <TextInput
                        name="orderContact.lastName"
                        label={strings["lastName"]}
                        placeholder={strings["lastName"]}
                        {...bind}
                        required /**/
                        onInput={(e) => {
                          if (e.target.value === "") {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12 col-xs-6 ">
                      <TextInput
                        name="orderContact.email"
                        label={strings["email"]}
                        placeholder={strings["email"]}
                        type="email"
                        {...bind}
                        required
                        onInput={(e) => {
                          if (e.target.value === "") {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else if (
                            !new RegExp(EMAIL_PATTERN).test(e.target.value)
                          ) {
                            e.target.setCustomValidity(
                              strings["errorInvalidFormat"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                        /* */
                      />
                    </div>
                    <div className="col col-12 col-xs-6 ">
                      <TextInput
                        name="orderContact.phone"
                        label={strings["telephone"]}
                        placeholder={strings["telephone"]}
                        type="tel"
                        {...bind}
                        required
                        onInput={(e) => {
                          if (e.target.value === "") {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else if (
                            !new RegExp(PHONE_PATTERN).test(e.target.value)
                          ) {
                            e.target.setCustomValidity(
                              strings["errorInvalidFormat"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col col-12 col-md-9 ">
                <div className="container">
                  <div className="row">
                    <div className="col col-12">
                      <h3 className="text lead">
                        {strings["invoiceInformation"]}
                      </h3>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12 col-xs-6 ">
                      <TextInput
                        name="billing.companyName"
                        label={strings["companyName"]}
                        placeholder={strings["companyName"]}
                        {...bind}
                        required
                        onInput={(e) => {
                          if (e.target.value === "") {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                      />
                    </div>
                    <div className="col col-12 col-xs-6 ">
                      <TextInput
                        name="billing.orgNr"
                        label={strings["vatOrgNr"]}
                        placeholder={strings["vatOrgNr"]}
                        {...bind}
                        required
                        onInput={(e) => {
                          if (e.target.value === "") {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12 col-xs-6 ">
                      <TextInput
                        name="billing.address"
                        label={strings["street"]}
                        placeholder={strings["street"]}
                        {...bind}
                        required
                        onInput={(e) => {
                          if (e.target.value === "") {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                      />
                    </div>
                    <div className="col col-12 col-xs-6 ">
                      <TextInput
                        name="billing.zip"
                        label={strings["zip"]}
                        placeholder={strings["zip"]}
                        {...bind}
                        required
                        onInput={(e) => {
                          if (e.target.value === "") {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12 col-xs-6 ">
                      <TextInput
                        name="billing.city"
                        label={strings["city"]}
                        placeholder={strings["city"]}
                        {...bind}
                        required
                        onInput={(e) => {
                          if (e.target.value === "") {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                      />
                    </div>
                    <div className="col col-12 col-xs-6 ">
                      <TextInput
                        name="billing.country"
                        label={strings["country"]}
                        placeholder={strings["country"]}
                        {...bind}
                        required
                        onInput={(e) => {
                          if (e.target.value === "") {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12 col-xs-6 ">
                      <TextInput
                        name="billing.purchaseOrderNr"
                        label={`${strings["purchaseOrderNr"]} (${strings["optional"]})`}
                        placeholder={strings["purchaseOrderNr"]}
                        {...bind}
                      />
                    </div>
                  </div>
                  {/*}
                            <div className='row'>
                                <div className='col col-12 col-xs-6 '>
                                        <div className='border-box'>
                                        <CheckBox name="billing.eInvoice" label="I want to receive an E-invovice *" {...bind} />
                                        </div>
                                        <br/>
                                        <p className='text base'>* If not possible, invoice will arrive by email</p>
                                </div>
                            </div>
                            {*/}
                </div>
              </div>
              <div className="col col-12 col-md-9 ">
                <div className="container">
                  <div className="row">
                    <div className="col col-12">
                      <h3 className="text lead">{strings["invoiceContact"]}</h3>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12">
                      <div
                        className="text base"
                        dangerouslySetInnerHTML={{
                          __html: strings["invoiceContactDescription"],
                        }}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col col-12 col-md-6">
                      <div className="border-box ">
                        <CheckBox
                          name="billing.reference.sameAsOrder"
                          label={strings["sameAsOrderContact"]}
                          onRender={invoiceContactRender}
                          {...bind}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12 col-xs-6 ">
                      <TextInput
                        name="billing.reference.firstName"
                        label={strings["firstName"]}
                        placeholder={strings["firstName"]}
                        disabled={invoiceRefSameAsOrder}
                        {...bind}
                        required={invoiceRefSameAsOrder ? false : true}
                        onInput={(e) => {
                          if (e.target.value === "" && !invoiceRefSameAsOrder) {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                      />
                    </div>
                    <div className="col col-12 col-xs-6 ">
                      <TextInput
                        name="billing.reference.lastName"
                        label={strings["lastName"]}
                        placeholder={strings["lastName"]}
                        disabled={invoiceRefSameAsOrder}
                        {...bind}
                        required={invoiceRefSameAsOrder ? false : true}
                        onInput={(e) => {
                          if (e.target.value === "" && !invoiceRefSameAsOrder) {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12 col-xs-6 ">
                      <TextInput
                        name="billing.reference.email"
                        label={strings["email"]}
                        placeholder={strings["email"]}
                        type="email"
                        disabled={invoiceRefSameAsOrder}
                        {...bind}
                        required={invoiceRefSameAsOrder ? false : true}
                        onInput={(e) => {
                          if (e.target.value === "") {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else if (
                            !new RegExp(EMAIL_PATTERN).test(e.target.value)
                          ) {
                            e.target.setCustomValidity(
                              strings["errorInvalidFormat"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                      />
                    </div>
                    <div className="col col-12 col-xs-6 ">
                      <TextInput
                        name="billing.reference.phone"
                        label={strings["telephone"]}
                        placeholder={strings["telephone"]}
                        type="tel"
                        disabled={invoiceRefSameAsOrder}
                        {...bind}
                        required={invoiceRefSameAsOrder ? false : true}
                        onInput={(e) => {
                          if (e.target.value === "") {
                            e.target.setCustomValidity(
                              strings["errorRequiredField"]
                            );
                          } else if (
                            !new RegExp(PHONE_PATTERN).test(e.target.value)
                          ) {
                            e.target.setCustomValidity(
                              strings["errorInvalidFormat"]
                            );
                          } else {
                            e.target.setCustomValidity("");
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col col-12 col-md-9 ">
                <div className="container">
                  <div className="row">
                    <div className="col col-12">
                      <h3 className="text lead">
                        {strings["additionalInformation"]}
                      </h3>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12">
                      <div
                        className="text base"
                        dangerouslySetInnerHTML={{
                          __html: strings["additionalInformationDescription"],
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12">
                      <TextArea
                        name="additionalInfo"
                        label={`${strings["additionalInformation"]} (${strings["optional"]})`}
                        placeholder={strings["additionalInformation"]}
                        maxLength="500"
                        {...bind}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="container ">
            <div className="row">
              <div className="col col-12">
                <div className="container">
                  <div className="row">
                    <div className="col col-12">
                      <h2 className="text lead large">
                        {strings["importantInformation"]}
                      </h2>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12 col-md-9">
                      <div
                        className="text base"
                        dangerouslySetInnerHTML={{
                          __html: strings["importantInformationDescription"],
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col col-12 col-md-9">
                <div className="container">
                  <div className="row">
                    <div className="col col-12">
                      <h2 className="text lead large">
                        {strings["termsAndConditions"]}
                      </h2>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12">
                      <div
                        className="text base"
                        dangerouslySetInnerHTML={{
                          __html: strings["termsAndConditionText"],
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col col-12 col-md-9">
                <div className="container">
                  <div className="row">
                    <div className="col col-12">
                      <h2 className="text lead large">
                        {strings["gdprTitle"]}
                      </h2>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12">
                      <div
                        className="text base"
                        dangerouslySetInnerHTML={{
                          __html: strings["gdprText"],
                        }}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-12">
                      <div className="border-box">
                        <CheckBox
                          name="terms"
                          label={strings["termsAgreementLabel"]}
                          {...bind}
                          required
                          onInvalid={(e) => {
                            if (e.target.checked === false) {
                              e.target.setCustomValidity(
                                strings["errorTermsCondition"]
                              );
                            } else {
                              e.target.setCustomValidity("");
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="container">
            <Hr className=" title horizontal divider--passive medium " />
            <div className="row " style={{ marginTop: "40px" }}>
              <div className="col col-12 col-xs-6 col-md-5 offset-md-3 ">
                <div className="container">
                  <div className="row">
                    <div
                      className="col col-12 "
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "16px",
                        flexWrap: "wrap",
                      }}
                    >
                      <p className="text lead bold large text-right">
                        {strings["totalPrice"]}
                      </p>
                      <p className="text lead bold xxxlarge">
                        {formatAmount(total)}
                        {market === "dk" ? ",-" : ":-"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col col-12 col-xs-6 col-md-4 ">
                <div className="container">
                  <div className="row">
                    <Button
                      label={strings["submit"] || ""}
                      className="col col-12 offset-md-reset button--large button--primary"
                      onClick={onSubmit}
                    />
                  </div>
                </div>
              </div>
            </div>
            {errorMessage && (
              <div className="row">
                <Alert
                  type="error"
                  text={strings[errorMessage] || errorMessage}
                  className="col col-12"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftcardForm;
